// oxlint-disable react/jsx-pascal-case
import { type Component, type ComponentProps, type JSX, Show } from "solid-js";
import { cn } from "@journal/shared";

interface EmptyStateProps {
  icon: Component<ComponentProps<"svg">>;
  title: string;
  description?: string;
  /**
   * Optional theme-aware SVG illustration that replaces the icon chip.
   * Adds editorial identity without sacrificing contrast; the chip remains
   * the default fallback.
   */
  illustration?: JSX.Element;
  /**
   * Actionable CTA (onboarding/first-run): a button or link that takes the user
   * out of the dead end (e.g. "escribe tu primera entrada").
   */
  action?: JSX.Element;
  /**
   * When defined, the icon chip/illustration becomes an interactive target
   * that triggers the same action as the primary CTA (open a dialog, navigate,
   * or focus an input) so the empty-state is never a dead end. Leave it
   * undefined in purely informational empty-states (the icon remains
   * decorative). Enter/Space work for free because it is a `<button>`.
   */
  onIconActivate?: () => void;
  /**
   * `aria-label` for the icon button (recommended when `onIconActivate` is
   * present). Describes the action, e.g. "Escribe tu primera entrada".
   */
  iconActionLabel?: string;
  class?: string;
}

export const EmptyState: Component<EmptyStateProps> = (props) => (
  <div class={cn("flex flex-1 flex-col items-center justify-center p-8 text-center", props.class)}>
    <div class="animate-scale-in">
      {/* When there is a wired action, the chip/illustration becomes a real
          <button> real para que tap/click/teclado (Enter/Space, nativos de
          <button>) disparen el CTA. Si no, queda decorativo. El anillo de foco
          es visible; la animación float ya está suprimida bajo el guard global
          de prefers-reduced-motion. */}
      <Show
        when={props.onIconActivate}
        fallback={
          <Show
            when={props.illustration}
            fallback={
              // Solid bg-accent chip + glyph in accent-foreground: guaranteed contrast
              // even with low-contrast custom accents (it does not rely on text-accent
              // over a muted surface).
              <div class="mb-6 rounded-full bg-accent p-6 text-accent-foreground shadow-md ring-1 ring-black/5 animate-float">
                <props.icon class="h-12 w-12" />
              </div>
            }
          >
            <div class="mb-5 animate-float text-primary">{props.illustration}</div>
          </Show>
        }
      >
        {(activate) => (
          <Show
            when={props.illustration}
            fallback={
              <button
                type="button"
                aria-label={props.iconActionLabel ?? props.title}
                onClick={() => {
                  activate()();
                }}
                class="group mb-6 rounded-full bg-accent p-6 text-accent-foreground shadow-md ring-1 ring-black/5 animate-float transition-[transform,box-shadow] duration-200 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-95 motion-safe:hover:scale-105"
              >
                <props.icon class="h-12 w-12" />
              </button>
            }
          >
            <button
              type="button"
              aria-label={props.iconActionLabel ?? props.title}
              onClick={() => {
                activate()();
              }}
              class="group mb-5 inline-flex animate-float rounded-full text-primary transition-transform duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-95 motion-safe:hover:scale-105"
            >
              {props.illustration}
            </button>
          </Show>
        )}
      </Show>
    </div>
    <h3 class="text-lg font-semibold tracking-tight">{props.title}</h3>
    <Show when={props.description}>
      <p class="mt-2 text-sm text-muted-foreground max-w-xs mx-auto">{props.description}</p>
    </Show>
    <Show when={props.action}>
      <div class="mt-5">{props.action}</div>
    </Show>
  </div>
);
